// Razorpay subscription checkout helpers for AV Simulation Studio.
// Loads the Checkout script on demand and runs the subscribe → checkout →
// verify round-trip. Keys are never hardcoded — the publishable key_id comes
// from the backend /api/billing/subscribe response.
import { tokenStore } from './tokenStore';

const API = process.env.REACT_APP_BACKEND_URL;
const CHECKOUT_SRC = 'https://checkout.razorpay.com/v1/checkout.js';

export function loadRazorpayScript() {
  return new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const s = document.createElement('script');
    s.src = CHECKOUT_SRC;
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });
}

export async function fetchBillingConfig() {
  try {
    const r = await fetch(`${API}/api/billing/config`);
    return await r.json();
  } catch {
    return { configured: false };
  }
}

/**
 * Start an auto-recurring subscription for the signed-in user.
 * @param {'monthly'|'yearly'} plan
 * @param {{name?:string,email?:string,contact?:string}} prefill
 * @returns {Promise<{status:'success'|'dismissed'|'error', data?:object, message?:string}>}
 */
export async function startSubscription(plan, prefill = {}) {
  const token = tokenStore.get();
  if (!token) return { status: 'error', message: 'Please sign in first' };

  const ok = await loadRazorpayScript();
  if (!ok) return { status: 'error', message: 'Could not load payment gateway' };

  // 1. Create the subscription server-side.
  const subRes = await fetch(`${API}/api/billing/subscribe`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify({ plan }),
  });
  const subData = await subRes.json().catch(() => ({}));
  if (!subRes.ok) {
    return { status: 'error', message: subData.detail || 'Could not start subscription' };
  }

  // 2. Open Razorpay Checkout and wait for the customer to authorize.
  return new Promise((resolve) => {
    const rzp = new window.Razorpay({
      key: subData.key_id,
      subscription_id: subData.subscription_id,
      name: 'AV Simulation Studio',
      description: plan === 'yearly' ? 'Pro Yearly' : 'Pro Monthly',
      image: '/branding/avst-logo.png',
      prefill,
      theme: { color: '#7c3aed' },
      handler: async (response) => {
        // 3. Verify the signature server-side and activate access.
        try {
          const vRes = await fetch(`${API}/api/billing/verify`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
            body: JSON.stringify({
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_subscription_id: response.razorpay_subscription_id,
              razorpay_signature: response.razorpay_signature,
            }),
          });
          const vData = await vRes.json().catch(() => ({}));
          if (!vRes.ok) return resolve({ status: 'error', message: vData.detail || 'Verification failed' });
          resolve({ status: 'success', data: vData });
        } catch (e) {
          resolve({ status: 'error', message: 'Verification request failed' });
        }
      },
      modal: {
        ondismiss: () => resolve({ status: 'dismissed' }),
      },
    });
    rzp.open();
  });
}
